import { AnalyticsService } from '@alison/analytics/client';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import PlayCircleOutlineOutlined from '@mui/icons-material/PlayCircleOutlineOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { DateWindow } from '../../lib/filters.hooks.js';
import {
  useBenchmarkCount,
  useChainNext,
  usePlatforms,
  useRunResult,
  useSession,
  useStartRun,
  useWorkflowDetail,
} from '../../lib/workflows.hooks.js';
import { AiInsightsChip } from './AiInsightsChip.js';
import { AiInsightsPanel } from './AiInsightsPanel.js';
import { AI_INSIGHTS, AI_INSIGHTS_HIDDEN, insightsEdge } from './aiInsightsOffer.js';
import { useAlisonForm } from './alison-run/AlisonRunProvider.js';
import { AutomateCard } from './automations/AutomateCard.js';
import { type AutomationRef, RunLabel } from './automations/RunLabel.js';
import { ConfigureStep } from './ConfigureStep.js';
import { CreativeDrawer } from './CreativeDrawer.js';
import {
  COMPARE_VALUES,
  COMPARISON_ACTION,
  COUNTRY_BREAKDOWN,
  CountryPairSetup,
  missingSides,
  type SidePair,
  sidesOf,
} from './comparison/CountryPairSetup.js';
import {
  comparisonResultSchema,
  type PickedElement,
  pickedElementsOf,
} from './comparison/comparisonResult.js';
import { FieldTooltip } from './FieldHeader.js';
import { FullAnalysisDrawer } from './FullAnalysisDrawer.js';
import {
  type FieldValue,
  type FieldValues,
  missingRequired,
  withFieldValue,
} from './fieldValues.js';
import { GoalChip, type GoalProgress } from './GoalChip.js';
import {
  briefNameOfKey,
  elementKeysOf,
  elementRefOfKey,
  keptKeysOf,
  narrowBriefs,
  sameKeys,
} from './ideation/brief/briefResult.js';
import { chainRowsOf } from './ideation/chainRows.js';
import { NextActionPicker, primaryAction } from './NextActionPicker.js';
import { OutcomeDrawer } from './OutcomeDrawer.js';
import { Pill, PRIMARY_PILL_SX } from './Pill.js';
import { parseResult } from './parsedResult.js';
import { byDisplayOrder } from './platformOrder.js';
import {
  configuredRuns,
  emptyConfig,
  missingConfig,
  platformsLine,
  restrictPlatforms,
  valuesFor,
} from './preflightConfig.js';
import { ResultView } from './ResultView.js';
import { RunCreatedToast, SAVED_TO_OUTCOMES } from './RunCreatedToast.js';
import { isIterationsResult, pickedForRun } from './recommendationRows.js';
import type { ReportScope } from './reportMeta.js';
import { summaryOf } from './resultRows.js';
import { isRisingResult } from './risingRows.js';
import { failureReason } from './runFailure.js';
import type { RunFilters } from './runFilters.js';
import { buildRunParams, fieldValuesFromParams, openingValues } from './runParams.js';
import { runInFlight, stepperState } from './runState.js';
import { SessionMap } from './SessionMap.js';
import { ShareRunButton } from './ShareRunButton.js';
import { StepFooter } from './StepFooter.js';
import { StepRunProgress } from './StepRunProgress.js';
import { RECALC_PREFLIGHT } from './scoredBatch.js';
import type { ChainParent, DoneRow, RanDisplay, RanStep } from './sessionRows.js';
import {
  briefVersion,
  extraMapOutcomes,
  historyFromSession,
  parentOf,
  savedOutcomes,
  savesBrief,
  sessionMapRows,
  visibleRows,
  withOutcomeDates,
} from './sessionRows.js';
import { preRunSummary, presetFieldLabel, presetLabelOf, stepSummaryLine } from './stepSummary.js';
import { UploadStep } from './UploadStep.js';
import { hasUploadField, uploadedLine, uploadedValues, videoCount } from './uploadRows.js';
import { useAiInsights } from './useAiInsights.js';
import { type FieldOptions, useFieldOptions } from './useFieldOptions.js';
import { usePreflightUpload } from './usePreflightUpload.js';
import { WorkflowStepCard } from './WorkflowStepCard.js';
import { stepperFlows, WorkflowStepper } from './WorkflowStepper.js';
import type {
  NextAction,
  SessionStep,
  WorkflowDetail as WorkflowDetailData,
  WorkflowStep,
} from './workflows.types.js';

const GUTTER_X = 34;
const UPDATE_BRIEF = 'updateIdeationBrief';
const FILL_ELEMENTS = 'fillElements';
// Renders take minutes: the progress screen offers a way back to the catalog.
const BACKGROUND_RUN_ACTIONS = new Set(['generateVideo', 'createVideo']);

const CONFIGURE_HINT =
  'What each creative is for. The fields are the ones each platform is actually scored on, so a creative is only judged against rules that apply to it.';

interface WorkflowDetailProps {
  workflowId: string;
  runId: string | null;
  dateWindow: DateWindow;
  accountIds: number[];
  availablePlatforms: string[];
  leadingKpi: string;
  filters: RunFilters;
  onRunStarted: () => void;
  onRunChange: (runId: string | null) => void;
  /** The workflow the user picked in the catalog when it starts from this one. */
  goal: string | null;
  onGoalClear: () => void;
  /** Hides the breadcrumb and session map, which lead to pages preflight-only clients can't open. */
  preflightOnly?: boolean;
}

interface ChainLink {
  actionType: string;
  label: string | null;
  // `params` is what the step behind this one decided on its behalf — the
  // recommendations picked for a brief, say — merged under the step's own
  // fields when it runs.
  // `inherited` is the parent step's own configuration; fields the next step
  // shares with it (a KPI, a benchmark gate) start from those values.
  parent:
    | (ChainParent & {
        selectItems: Record<string, unknown>[];
        params: Record<string, unknown>;
        inherited: Record<string, unknown>;
      })
    | null;
}

interface Past {
  link: ChainLink;
  ran: RanStep | null;
  historyLength: number;
}

type Advance = (
  next: NextAction,
  selectItems: Record<string, unknown>[],
  params: Record<string, unknown>,
  rows: DoneRow[],
  parentRunId: string,
  inherited: Record<string, unknown>,
  briefRunId: string | null,
) => void;

interface ActiveStepProps {
  link: ChainLink;
  scope: ReportScope | null;
  ran: RanStep | null;
  params: Record<string, unknown> | null;
  onRan: (ran: RanStep | null) => void;
  doneIndex: number;
  dateWindow: DateWindow;
  accountIds: number[];
  availablePlatforms: string[];
  leadingKpi: string;
  filters: RunFilters;
  before: ReactNode;
  onAdvance: Advance;
  onRunStarted: () => void;
  preferredActionType: string | null;
  /** The goal's path is still loading, so the lead action is not known yet. */
  goalPending: boolean;
}

interface StepFormProps extends ActiveStepProps {
  step: WorkflowStep;
  resultStep: WorkflowStep | undefined;
  shareable: boolean;
  labels: Record<string, string>;
}

function seededParent(steps: SessionStep[], index: number): ChainLink['parent'] {
  const parent = parentOf(steps, index);
  if (parent === null) return null;
  const inherited = steps.find((step) => step.runId === parent.runId)?.params ?? {};
  return { ...parent, selectItems: [], params: {}, inherited };
}

// A parent taken from step order never names the brief an update replaced.
function versionOf(
  actionType: string,
  parent: ChainLink['parent'],
  landedAt: string | null,
): ReturnType<typeof briefVersion> {
  return briefVersion(
    actionType,
    parent === null || parent.assumed ? null : (parent.briefRunId ?? parent.runId),
    landedAt,
  );
}

// The service ticks the rows its result already stands for: the gems in the brief, every
// addition it offers.
function preselectedIds(rows: { id: string; values: Record<string, unknown> }[]): string[] {
  return rows.filter((row) => row.values['selected'] === true).map((row) => row.id);
}

// One element of a brief whatever network a row reports it on.
function elementIdOf(values: Record<string, unknown>): string {
  return JSON.stringify([values['feature'], values['scene'] ?? null, values['tag']]);
}

function describeValues(
  step: WorkflowStep,
  values: FieldValues,
  optionsFor: FieldOptions,
): RanDisplay {
  const preset = presetLabelOf(step, values);
  const parts = preRunSummary(step, values, optionsFor).parts;
  return {
    title: presetFieldLabel(step) ?? step.title,
    line: preset,
    panel:
      parts.length === 0
        ? null
        : { label: preset === null ? step.settingsTitle : `${preset} preset`, parts },
  };
}

function selectionLine(selected: number, total: number): string {
  if (selected === 0) return `${total} ${total === 1 ? 'item' : 'items'}`;
  return `${selected} of ${total} selected`;
}

function goalProgress(
  goal: WorkflowDetailData | undefined,
  entryId: string,
  actionType: string,
): GoalProgress | null {
  const path = goal?.entryPoints.find((entry) => entry.workflowId === entryId)?.path;
  const step = path?.indexOf(actionType) ?? -1;
  if (goal === undefined || path === undefined || step < 0) return null;
  return { name: goal.name, step, total: path.length, next: path[step + 1] ?? null };
}

function Breadcrumb({
  name,
  goal,
  automation,
}: {
  name: string;
  goal: GoalProgress | null;
  automation: AutomationRef | null;
}): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        gap: 1,
        px: 6,
        py: 3,
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'background.page',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
        <Link
          component={RouterLink}
          to="/workflows"
          sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 13, fontWeight: 600 }}
        >
          <ArrowBackOutlined sx={{ fontSize: 16 }} />
          Workflows
        </Link>
        <ChevronRightOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{name}</Typography>
        {automation === null ? null : <RunLabel automation={automation} />}
        {goal === null ? null : <GoalChip progress={goal} />}
      </Stack>

      <Button
        sx={{ p: 1.5 }}
        component={RouterLink}
        to="/workflows"
        variant="outlined"
        size="small"
      >
        Switch workflow
      </Button>
    </Stack>
  );
}

function StepForm({
  step,
  resultStep,
  shareable,
  labels,
  ran,
  params: ranParams,
  onRan,
  link,
  dateWindow,
  accountIds,
  availablePlatforms,
  leadingKpi,
  filters,
  before,
  onAdvance,
  doneIndex,
  onRunStarted,
  scope,
  preferredActionType,
  goalPending,
}: StepFormProps): ReactNode {
  const [edited, setEdited] = useState<FieldValues | null>(null);
  const runId = ran?.runId ?? null;
  const [rowSelection, setRowSelection] = useState<{
    runId: string | null;
    ids: string[];
  } | null>(null);
  const [picked, setPicked] = useState<Record<string, number[]>>({});
  const [elementSelection, setElementSelection] = useState<{
    runId: string | null;
    keys: string[];
  } | null>(null);
  const [comparedKeys, setComparedKeys] = useState<string[]>([]);
  const [chosenType, setChosenType] = useState<string | null>(null);
  // A run this form opened on (a shared or reopened link) was not just created here.
  const [dismissedRunId, setDismissedRunId] = useState<string | null>(ran?.runId ?? null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [scored, setScored] = useState<string | null>(null);
  const [scoredNetwork, setScoredNetwork] = useState<number | null>(null);
  const openScored = (creativeId: string, networkId: number): void => {
    setScored(creativeId);
    setScoredNetwork(networkId);
  };
  const trackCreativeOpened = (source: 'result_page' | 'full_report'): void =>
    AnalyticsService.track('Workflow - Creative Opened', { workflowId: link.actionType, source });
  const upload = usePreflightUpload();
  const uploading = hasUploadField(step.fields);
  const [configuring, setConfiguring] = useState(false);
  const [storedConfig, setConfig] = useState(emptyConfig());
  const [openCreative, setOpenCreative] = useState<string | null>(null);
  const [openPlatform, setOpenPlatform] = useState<string | null>(null);
  const platforms = byDisplayOrder(usePlatforms(uploading)).filter((platform) =>
    availablePlatforms.includes(platform.slug),
  );
  const config = restrictPlatforms(
    storedConfig,
    platforms.map((platform) => platform.slug),
  );
  if (config !== storedConfig) setConfig(config);
  const landed = upload.rows.filter((row) => row.status === 'done');
  // Falls back to the first that landed, so removing the open one does not
  // leave the fields pointing at a creative that is gone.
  const openId = landed.some((row) => row.key === openCreative)
    ? (openCreative ?? '')
    : (landed[0]?.key ?? '');
  const firstPicked = platforms.find((platform) => config.platforms.includes(platform.slug));
  const pickedPlatform =
    platforms.find(
      (platform) => platform.slug === openPlatform && config.platforms.includes(platform.slug),
    ) ?? firstPicked;
  const benchmark = useBenchmarkCount(
    dateWindow,
    filters,
    valuesFor(config, openId, pickedPlatform?.slug ?? ''),
    uploading && configuring,
  );
  const entered =
    edited ?? openingValues(step.fields, ranParams, link.parent?.inherited ?? {}, leadingKpi);
  // A new comparison started here is Country A vs Country B; one chained in keeps the full form.
  const compareField = step.fields.find((field) => field.key === COMPARE_VALUES);
  const countryPair =
    link.parent === null &&
    step.submit?.actionId === COMPARISON_ACTION &&
    compareField !== undefined;
  const [sides, setSides] = useState<SidePair>(() => sidesOf(entered[COMPARE_VALUES]));
  // A required upload is met by the files that landed, which live in the upload
  // screen's own state; without this the step reports Creatives missing forever.
  const values = uploading
    ? { ...entered, ...uploadedValues(step.fields, upload.rows) }
    : countryPair
      ? {
          ...entered,
          filter_breakdown: COUNTRY_BREAKDOWN,
          [COMPARE_VALUES]: sides.filter((side) => side !== ''),
        }
      : entered;
  const optionsFor = useFieldOptions(step, values, dateWindow, accountIds, leadingKpi);
  const summary = preRunSummary(step, values, optionsFor);
  const setValue = (key: string, value: FieldValue): void =>
    setEdited(withFieldValue(step.fields, values, key, value));
  const navigate = useNavigate();
  // Leaving on purpose: the page's leave guard is for stray navigation.
  const viewOutcomes = (runId: string): void =>
    void navigate({ to: '/outcomes', search: { openRunId: runId }, ignoreBlocker: true });
  const returnToWorkflows = (): void =>
    void navigate({
      to: '/workflows',
      ignoreBlocker: true,
      search: (previous) =>
        Object.fromEntries(
          Object.entries(previous).filter(([key]) => key !== 'run' && key !== 'goal'),
        ),
    });
  const startRun = useStartRun();
  const chainNext = useChainNext();
  const run = useRunResult(runId);
  const sourceRun = useRunResult(
    link.actionType === 'generateIdeationBrief' ? (link.parent?.runId ?? null) : null,
  ).data;
  const record = run.data ?? null;
  const finished = record !== null && record.status === 'completed' ? record : null;
  const running =
    startRun.isPending ||
    chainNext.isPending ||
    runInFlight(runId !== null, record?.status ?? null, run.isError);
  const inProgress = finished === null && running;
  const insightsParent =
    !AI_INSIGHTS_HIDDEN && finished !== null && insightsEdge(finished) !== null
      ? finished.runId
      : null;
  const insightsDetail = useWorkflowDetail(insightsParent === null ? null : AI_INSIGHTS);
  const [insightsStep, insightsResultStep] = insightsDetail.data?.steps ?? [];
  const insightsLabels = insightsDetail.data?.labels ?? null;
  // Only a service that keeps insights as asides describes the panel; an older one would
  // start a new paid run on every reload, so it gets no chip.
  const insightsLabel = insightsLabels?.['panel_title'] ?? null;
  const doneLine = stepSummaryLine(step, values);
  // A chained step with nothing to configure starts on arrival and has no settings step of its own.
  const startsOnArrival = link.parent !== null && step.fields.length === 0;
  const configDoneRow = (runId: string): DoneRow => ({
    key: `${runId}-config`,
    title: presetFieldLabel(step) ?? step.title,
    line: doneLine,
    panel: ran?.display?.panel ?? null,
    outcome: null,
    hidden: startsOnArrival,
  });
  const describe = (): RanDisplay => describeValues(step, values, optionsFor);

  const missingFields = countryPair
    ? [
        ...missingRequired(
          step.fields.filter((field) => field !== compareField),
          values,
        ),
        ...missingSides(sides, [
          labels['side_a'] ?? compareField.label,
          labels['side_b'] ?? compareField.label,
        ]),
      ]
    : missingRequired(step.fields, values);
  // Sentences of their own, after the field list.
  const blockers = [
    ...(configuring ? missingConfig(config) : []),
    ...(configuring &&
    config.scoringMode !== 'best_practice' &&
    !benchmark.isEnough &&
    !benchmark.isFetching
      ? [`Upload at least ${benchmark.minimum} creatives to compare against.`]
      : []),
  ];
  const missing = [...missingFields, ...blockers];
  const missingHint = [
    ...(missingFields.length === 0 ? [] : [`Fill in ${missingFields.join(', ')}.`]),
    ...blockers,
  ].join(' ');
  const resultData = finished === null ? null : parseResult(finished.result, finished.resultKind);
  const rows = resultData?.kind === 'other' ? resultData.rows : [];
  // AI Insights is not a chain step, so it never leads the footer. A country pair ends
  // on its result for now, so it offers nothing to pick or continue into.
  const offered = countryPair
    ? []
    : (finished?.nextActions ?? []).filter(
        (action) => action.actionType !== RECALC_PREFLIGHT && action.actionType !== AI_INSIGHTS,
      );
  const chainRows =
    finished === null || resultData === null
      ? []
      : chainRowsOf(finished.result, finished.resultKind, resultData);
  const selectable = chainRows.length > 0 && offered.some((action) => action.requiresSelection);
  const defaultIds = preselectedIds(chainRows);
  const creativeIds = rowSelection?.runId === runId ? rowSelection.ids : defaultIds;
  const setCreativeIds = (next: (current: string[]) => string[]): void =>
    setRowSelection((current) => ({
      runId,
      ids: next(current?.runId === runId ? current.ids : defaultIds),
    }));
  // A list that found nothing has nothing to chain, so the chain ends there.
  const noResults =
    (finished?.resultKind === 'creative_list' ||
      finished?.resultKind === 'element_list' ||
      finished?.resultKind === 'combination_list') &&
    chainRows.length === 0;
  // On an iterations result the analyst picks recommendations, not creatives:
  // a creative is selected because something inside it was, so the creative
  // selection is read off the picks rather than kept beside them.
  const briefing = finished !== null && isIterationsResult(finished.result);
  // Elements are picked per brief and the storyboard step takes one brief, so
  // picking into another brief drops the first.
  const ideating = resultData?.kind === 'ideation_brief';
  const briefs =
    resultData?.kind === 'ideation_brief' && resultData.parsed.success
      ? resultData.parsed.data.briefs
      : [];
  const briefElementKeys = briefs.flatMap(elementKeysOf);
  const keptKeys = briefs.flatMap(keptKeysOf);
  // A generated brief offers its suggestions ticked; an updated one ticks what it kept,
  // so its own leftover suggestions do not read as a change.
  const elementKeys =
    elementSelection?.runId === runId
      ? elementSelection.keys
      : briefs.length === 1
        ? finished?.actionType === UPDATE_BRIEF
          ? keptKeys
          : briefElementKeys
        : [];
  const updateEdge = ideating
    ? offered.find((action) => action.actionType === UPDATE_BRIEF)
    : undefined;
  // The brief moves on to a storyboard only once it holds the elements ticked here.
  const briefChanged = updateEdge !== undefined && !sameKeys(elementKeys, keptKeys);
  const addedTags = new Set(elementKeys.filter((key) => !keptKeys.includes(key))).size;
  // Off gems or fill results the picked rows are what an update applies to the brief.
  const picksForBrief = !ideating && offered.some((action) => action.actionType === UPDATE_BRIEF);
  const picksChanged = picksForBrief && !sameKeys(creativeIds, defaultIds);
  // Fill rows are never in the brief yet; a gem is when the service ticked it.
  const addedPicks = new Set(
    chainRows
      .filter(
        (row) =>
          creativeIds.includes(row.id) &&
          (resultData?.kind === 'element_pool' || row.values['selected'] !== true),
      )
      .map((row) => elementIdOf(row.values)),
  ).size;
  const pickFooter = offered.filter((action) =>
    picksChanged ? action.actionType !== FILL_ELEMENTS : action.actionType !== UPDATE_BRIEF,
  );
  const footerActions = briefChanged
    ? [updateEdge]
    : picksForBrief
      ? pickFooter.length > 0
        ? pickFooter
        : offered
      : ideating
        ? // An unchanged brief leads to its storyboard; Fill Elements stays in the menu.
          offered
            .filter((action) => action.actionType !== UPDATE_BRIEF)
            .sort(
              (a, b) =>
                Number(a.actionType === FILL_ELEMENTS || !a.enabled) -
                Number(b.actionType === FILL_ELEMENTS || !b.enabled),
            )
        : offered.filter((action) => action.actionType !== UPDATE_BRIEF);
  const chosen = goalPending
    ? undefined
    : primaryAction(footerActions, chosenType, preferredActionType);
  const setElementKeys = (keys: string[]): void => setElementSelection({ runId, keys });
  const selectedIds = briefing
    ? rows
        .filter((row) => (row.creativeId === null ? [] : (picked[row.creativeId] ?? [])).length > 0)
        .map((row) => row.id)
    : ideating
      ? [...new Set(elementKeys.map(briefNameOfKey))]
      : creativeIds;
  // AI Insights runs on the ticked rows, or on every row the edge takes when none are.
  const insightsPicks = ideating
    ? []
    : chainRows.flatMap((row, index) =>
        selectedIds.includes(row.id) ? [{ position: index + 1, values: row.values }] : [],
      );
  const insightsCap = finished === null ? null : (insightsEdge(finished)?.maxSelection ?? null);
  const insights = useAiInsights(
    insightsParent,
    insightsPicks.length > 0
      ? {
          items: insightsPicks.map((pick) => pick.values),
          positions: insightsPicks.map((pick) => pick.position),
        }
      : {
          items: [],
          positions: chainRows.map((_, index) => index + 1).slice(0, insightsCap ?? undefined),
        },
  );
  const toggle = (id: string): void =>
    setCreativeIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );
  const toggleElement = (key: string): void =>
    setElementKeys(
      elementKeys.includes(key)
        ? elementKeys.filter((entry) => entry !== key)
        : [...elementKeys.filter((entry) => briefNameOfKey(entry) === briefNameOfKey(key)), key],
    );
  const toggleElements = (keys: string[]): void => {
    const rest = elementKeys.filter((entry) => !keys.includes(entry));
    const brief = keys[0] === undefined ? null : briefNameOfKey(keys[0]);
    setElementKeys(
      keys.every((key) => elementKeys.includes(key))
        ? rest
        : [...rest.filter((entry) => briefNameOfKey(entry) === brief), ...keys],
    );
  };
  const toggleCompared = (key: string): void =>
    setComparedKeys((current) =>
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key],
    );
  // A comparison edge that takes no creatives takes the picked tags instead.
  const takesElements = (action: NextAction): boolean =>
    finished?.resultKind === 'comparison' && !action.requiresSelection;
  const comparedElements = (): PickedElement[] => {
    const parsed = comparisonResultSchema.safeParse(finished?.result);
    return parsed.success ? pickedElementsOf(parsed.data, comparedKeys) : [];
  };
  const toggleAll = (): void =>
    setCreativeIds((current) =>
      current.length === chainRows.length ? [] : chainRows.map((row) => row.id),
    );
  const toggleGemsAll = (keys: string[]): void =>
    setCreativeIds((current) => (keys.every((key) => current.includes(key)) ? [] : keys));
  const togglePick = (creativeId: string, recommendationId: number): void =>
    setPicked((current) => {
      const ids = current[creativeId] ?? [];
      return {
        ...current,
        [creativeId]: ids.includes(recommendationId)
          ? ids.filter((entry) => entry !== recommendationId)
          : [...ids, recommendationId],
      };
    });
  const togglePickAll = (creativeId: string, all: number[]): void =>
    setPicked((current) => ({
      ...current,
      [creativeId]: (current[creativeId] ?? []).length === all.length ? [] : all,
    }));

  const params = uploading
    ? {
        ...buildRunParams(step.fields, values),
        scoring_mode: config.scoringMode,
        creatives: landed.map((row) => ({
          creative_id: row.key,
          creative_type: row.contentType === 'video/mp4' ? 'Video' : 'Banner',
          name: row.fileName,
          // The bucket is the server's to know, so it maps this to the paths
          // the run reads.
          object_name: row.objectName,
          runs: configuredRuns(config, row.key),
        })),
      }
    : countryPair
      ? {
          ...buildRunParams(step.fields, values),
          filter_breakdown: COUNTRY_BREAKDOWN,
          [COMPARE_VALUES]: sides,
        }
      : buildRunParams(step.fields, values);

  useAlisonForm(
    inProgress || finished !== null
      ? null
      : {
          actionType: step.submit?.actionId ?? link.actionType,
          params: { ...link.parent?.params, ...params },
          ...(link.parent === null
            ? {}
            : {
                parent: { runId: link.parent.runId, selectItems: link.parent.selectItems },
              }),
          missing: missingFields,
          blockers: [...blockers, ...(uploading && !configuring ? missingConfig(config) : [])],
        },
  );

  const submit = (): void => {
    const spec = step.submit;
    if (spec === null || (uploading && missingConfig(config).length > 0)) return;
    onRan(null);
    const started = {
      onSuccess: ({ runId: id }: { runId: string }) => {
        onRan({ runId: id, display: describe() });
        onRunStarted();
      },
    };

    if (link.parent === null) {
      startRun.mutate(
        {
          actionType: spec.actionId,
          scope: {
            dateRange: dateWindow,
            ...(accountIds.length > 0 ? { accountIds } : {}),
            ...(Object.keys(filters).length > 0 ? { filters } : {}),
          },
          params,
        },
        started,
      );
      return;
    }

    chainNext.mutate(
      {
        runId: link.parent.runId,
        actionType: spec.actionId,
        selectItems: link.parent.selectItems,
        paramsOverride: { ...link.parent.params, ...params },
      },
      started,
    );
  };

  const autoRan = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: submit is rebuilt every render; the ref makes this fire once
  useEffect(() => {
    if (!startsOnArrival || runId !== null || autoRan.current) return;
    autoRan.current = true;
    submit();
  }, [startsOnArrival, runId]);

  return (
    <>
      <Stack sx={{ flex: 1, gap: 4, px: GUTTER_X, py: 5 }}>
        {before}

        {finished === null && configuring && !inProgress ? (
          <Stack sx={{ gap: 2 }}>
            <Pill sx={[PRIMARY_PILL_SX, { alignSelf: 'flex-start' }]}>STEP {doneIndex + 1}</Pill>
            <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
              <Typography variant="h3" sx={{ fontSize: 22 }}>
                Set configurations
              </Typography>
              <FieldTooltip tooltip={CONFIGURE_HINT} />
            </Stack>
          </Stack>
        ) : null}

        {finished === null && !configuring && !inProgress ? (
          <WorkflowStepCard
            step={step}
            stepNumber={doneIndex}
            values={values}
            onChange={setValue}
            optionsFor={optionsFor}
            disabled={running}
            chained={link.parent !== null}
            {...(countryPair
              ? {
                  controls: (
                    <CountryPairSetup
                      labels={labels}
                      sides={sides}
                      resolved={optionsFor(compareField)}
                      onChange={setSides}
                    />
                  ),
                }
              : {})}
          />
        ) : null}

        {finished === null && uploading && !configuring && !inProgress ? (
          <UploadStep {...upload} />
        ) : null}

        {finished === null && uploading && configuring && !inProgress ? (
          <ConfigureStep
            config={config}
            platforms={platforms}
            creatives={landed.map((row) => ({
              id: row.key,
              name: row.fileName,
              type: row.contentType === 'video/mp4' ? 'Video' : 'Banner',
            }))}
            picked={openId}
            platform={openPlatform}
            benchmark={benchmark}
            onChange={setConfig}
            onPick={setOpenCreative}
            onPickPlatform={setOpenPlatform}
            onAddMore={() => setConfiguring(false)}
            onRename={upload.onRename}
          />
        ) : null}

        {finished === null || finished.outcomeKind === null ? null : (
          <RunCreatedToast
            open={finished.runId !== dismissedRunId}
            actionName={finished.actionName}
            outcomeKind={finished.outcomeKind}
            detail={SAVED_TO_OUTCOMES}
            onView={() => {
              setDismissedRunId(finished.runId);
              viewOutcomes(finished.runId);
            }}
            onClose={() => setDismissedRunId(finished.runId)}
          />
        )}

        {startRun.isError || chainNext.isError ? (
          <Alert severity="error">
            {startRun.error?.message ??
              chainNext.error?.message ??
              'This run could not be started.'}
          </Alert>
        ) : null}
        {run.isError ? <Alert severity="error">This run could not be read.</Alert> : null}
        {record?.status === 'failed' ? (
          <Alert severity="error">{failureReason(record.errorMessage) ?? 'This run failed.'}</Alert>
        ) : null}

        {inProgress ? (
          <StepRunProgress
            runningTitle={step.submit?.runningTitle ?? step.title}
            runningSteps={step.submit?.runningSteps ?? []}
            progress={record?.progress ?? null}
            {...(BACKGROUND_RUN_ACTIONS.has(step.submit?.actionId ?? '')
              ? { backgroundHref: '/workflows' }
              : {})}
          />
        ) : null}

        {finished === null &&
        sourceRun?.resultKind === 'element_list' &&
        (link.parent?.selectItems.length ?? 0) > 0 ? (
          <ResultView
            run={{
              ...sourceRun,
              result: isRisingResult(sourceRun.result)
                ? { result_type: 'rising_elements', elements: link.parent?.selectItems }
                : {
                    gems: link.parent?.selectItems.map((gem) => ({
                      ...gem,
                      // Scan-wide aggregates do not describe the selected subset.
                      category_gem_count: null,
                      category_best_uplift: null,
                    })),
                  },
            }}
          />
        ) : null}

        {finished === null ? null : (
          <Stack sx={{ gap: 2 }}>
            <Pill sx={[PRIMARY_PILL_SX, { alignSelf: 'flex-start' }]}>
              STEP {doneIndex + (uploading ? 2 : startsOnArrival ? 0 : 1)}
            </Pill>
            <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
              <Typography variant="h3" sx={{ fontSize: 22 }}>
                {resultStep?.title ?? finished.actionName}
              </Typography>
              <FieldTooltip tooltip={resultStep?.tooltip ?? null} />
              {insightsLabel === null ? null : (
                <AiInsightsChip
                  label={insightsLabel}
                  open={insights.open}
                  onToggle={insights.toggle}
                />
              )}
            </Stack>
            {resultStep?.description === undefined || resultStep.description === null ? null : (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {resultStep.description}
              </Typography>
            )}
          </Stack>
        )}

        {insightsLabels === null || !insights.open ? null : (
          <AiInsightsPanel
            labels={insightsLabels}
            tooltip={insightsResultStep?.tooltip ?? null}
            runningSteps={insightsStep?.submit?.runningSteps ?? []}
            run={insights.run}
            pending={insights.pending}
            error={insights.error}
            onRetry={insights.retry}
            onClose={insights.close}
          />
        )}

        {finished === null || resultData === null ? null : (
          <ResultView
            run={finished}
            resultData={resultData}
            collapsibleWhy
            creativeDetail={{
              scope: scope ?? {
                startDate: dateWindow[0],
                endDate: dateWindow[1],
                accountIds,
                filters,
              },
              leadingKpi,
            }}
            emptyState={resultStep?.emptyState ?? null}
            configStep={step}
            onEditConfig={() => {
              // Keeps the values this run used; they come from its session row, which clears with ran.
              setEdited(values);
              onRan(null);
            }}
            {...(finished.outcomeKind === null
              ? {}
              : {
                  onViewBrief: (name: string) => {
                    AnalyticsService.track('Workflow - Brief Viewed', {
                      workflowId: link.actionType,
                    });
                    setViewing(name);
                  },
                })}
            onOpenFullAnalysis={() => {
              AnalyticsService.track('Workflow - Full Analysis Opened', {
                workflowId: link.actionType,
              });
              setAnalysing(true);
            }}
            onOpenCreative={(...args: Parameters<typeof openScored>) => {
              trackCreativeOpened('result_page');
              openScored(...args);
            }}
            {...(selectable && !briefing && !ideating
              ? { selection: { selectedIds, onToggle: toggle, onToggleAll: toggleAll } }
              : {})}
            {...(selectable && briefing
              ? { picks: { picked, onToggle: togglePick, onToggleAll: togglePickAll } }
              : {})}
            {...(selectable && ideating
              ? {
                  elementSelection: {
                    selectedKeys: elementKeys,
                    onToggle: toggleElement,
                    onToggleAll: toggleElements,
                  },
                }
              : {})}
            {...(finished.resultKind === 'comparison' && !countryPair
              ? { elementPicks: { picked: comparedKeys, onToggle: toggleCompared } }
              : {})}
            {...(finished.resultKind === 'element_list' ||
            finished.resultKind === 'combination_list'
              ? {
                  gemPicks: {
                    picked: selectedIds,
                    onToggle: toggle,
                    onToggleAll: toggleGemsAll,
                  },
                }
              : {})}
          />
        )}
      </Stack>

      {finished !== null && !goalPending && (offered.length === 0 || noResults) ? (
        <StepFooter
          gutterX={GUTTER_X}
          title={summaryOf(finished.result) ?? doneLine}
          detail={resultStep?.description ?? null}
        >
          {shareable ? <ShareRunButton runId={finished.runId} /> : null}
          {finished.outcomeKind === null ? null : (
            <Button variant="outlined" onClick={() => viewOutcomes(finished.runId)}>
              View Outcomes
            </Button>
          )}
          <Button variant="gradient" onClick={returnToWorkflows}>
            Return to Workflows Screen
          </Button>
        </StepFooter>
      ) : null}

      {finished !== null && !goalPending && !noResults && chosen !== undefined ? (
        <StepFooter
          gutterX={GUTTER_X}
          title={summaryOf(finished.result) ?? doneLine}
          detail={resultStep?.description ?? null}
        >
          {shareable ? <ShareRunButton runId={finished.runId} /> : null}
          <NextActionPicker
            actions={footerActions}
            chosen={chosen}
            selectedCount={selectedIds.length}
            ctaCount={
              briefChanged
                ? addedTags
                : picksForBrief && chosen?.actionType === UPDATE_BRIEF
                  ? addedPicks
                  : selectedIds.length
            }
            onChoose={(action) => setChosenType(action.actionType)}
            onRun={(action) =>
              onAdvance(
                action,
                // Off gems, Fill Elements reads the run's brief and takes no rows.
                takesElements(action) || (action.actionType === FILL_ELEMENTS && !ideating)
                  ? []
                  : chainRows
                      .filter((row) => selectedIds.includes(row.id))
                      .map((row) => row.values),
                // The service takes a brief run's `briefs` from the run itself for both
                // brief steps; only a storyboard is handed the ticked elements.
                takesElements(action)
                  ? { elements: comparedElements() }
                  : briefing
                    ? { recommendation_ids: pickedForRun(rows, picked) }
                    : !ideating
                      ? {}
                      : action.actionType === UPDATE_BRIEF
                        ? { keep: elementKeys.map(elementRefOfKey) }
                        : action.actionType === FILL_ELEMENTS
                          ? {}
                          : { briefs: narrowBriefs(finished.result, elementKeys) },
                [
                  configDoneRow(finished.runId),
                  {
                    key: `${finished.runId}-result`,
                    title: resultStep?.title ?? step.title,
                    line: ideating
                      ? selectionLine(elementKeys.length, briefElementKeys.length)
                      : selectionLine(selectedIds.length, chainRows.length),
                    panel: null,
                    outcome:
                      finished.outcomeKind === null
                        ? null
                        : {
                            runId: finished.runId,
                            name: finished.outcomeTitle,
                            kind: finished.outcomeKind,
                            date: null,
                            ...versionOf(finished.actionType, link.parent, finished.completedAt),
                          },
                    extraOutcomes: extraMapOutcomes(finished, finished.extraOutcomes, null),
                    hidden: false,
                  },
                ],
                finished.runId,
                buildRunParams(step.fields, values),
                savesBrief(finished.outcomeKind, finished.extraOutcomes)
                  ? finished.runId
                  : (link.parent?.briefRunId ?? null),
              )
            }
          />
        </StepFooter>
      ) : null}

      {step.submit === null || finished !== null || inProgress ? null : (
        <StepFooter
          gutterX={GUTTER_X}
          title={summary.title}
          parts={
            uploading
              ? [
                  { text: uploadedLine(landed.length, videoCount(landed)), tooltip: null },
                  { text: platformsLine(config.platforms.length), tooltip: null },
                ]
              : summary.parts
          }
        >
          {uploading && !configuring ? (
            <Tooltip title={missingHint}>
              <span>
                <Button
                  variant="gradient"
                  disabled={missing.length > 0}
                  onClick={() => setConfiguring(true)}
                  endIcon={<ArrowForwardOutlined sx={{ fontSize: 16 }} />}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Next, configure details
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={missingHint}>
              <span>
                <Button
                  variant="gradient"
                  disabled={running || missing.length > 0}
                  onClick={submit}
                  startIcon={
                    running ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <PlayCircleOutlineOutlined sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  {step.submit.label}
                </Button>
              </span>
            </Tooltip>
          )}
        </StepFooter>
      )}

      <CreativeDrawer
        open={scored !== null && finished !== null}
        runId={finished?.runId ?? null}
        result={finished?.result ?? null}
        creativeId={scored}
        networkId={scoredNetwork}
        onOpenCreative={openScored}
        onClose={() => setScored(null)}
      />

      <FullAnalysisDrawer
        open={analysing && finished !== null}
        runId={finished?.runId ?? null}
        result={finished?.result ?? null}
        completedAt={finished?.completedAt ?? null}
        onClose={() => setAnalysing(false)}
        onCreativeOpened={() => trackCreativeOpened('full_report')}
      />

      <OutcomeDrawer
        focus={viewing}
        outcome={
          viewing === null || finished === null || finished.outcomeKind === null
            ? null
            : {
                runId: finished.runId,
                name: finished.outcomeTitle,
                kind: finished.outcomeKind,
                date: null,
              }
        }
        scope={scope}
        leadingKpi={leadingKpi}
        onClose={() => setViewing(null)}
      />
    </>
  );
}

function ActiveStep(props: ActiveStepProps): ReactNode {
  const detail = useWorkflowDetail(
    props.link.actionType,
    (props.link.parent?.selectItems.length ?? 0) > 0,
  );
  const [step, resultStep] = detail.data?.steps ?? [];

  if (detail.isPending) {
    return (
      <>
        <Stack sx={{ flex: 1, gap: 4, px: GUTTER_X, py: 5 }}>{props.before}</Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
        {props.link.label === null ? null : (
          <StepFooter gutterX={GUTTER_X} title={`Opening ${props.link.label}`}>
            <Button
              variant="gradient"
              disabled
              startIcon={<CircularProgress size={14} color="inherit" />}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Run {props.link.label}
            </Button>
          </StepFooter>
        )}
      </>
    );
  }

  if (detail.isError || step === undefined) {
    return (
      <Stack sx={{ flex: 1, gap: 4, px: GUTTER_X, py: 5 }}>
        {props.before}
        <Alert severity="error">This step could not be loaded.</Alert>
      </Stack>
    );
  }

  return (
    <StepForm
      {...props}
      step={step}
      resultStep={resultStep}
      shareable={detail.data?.shareable ?? false}
      labels={detail.data?.labels ?? {}}
    />
  );
}

export function WorkflowDetail({
  workflowId,
  runId,
  dateWindow,
  accountIds,
  availablePlatforms,
  leadingKpi,
  filters,
  onRunStarted,
  onRunChange,
  goal,
  onGoalClear,
  preflightOnly = false,
}: WorkflowDetailProps): ReactNode {
  const navigate = useNavigate();
  const root = useWorkflowDetail(workflowId);
  const goalId = goal !== null && goal !== workflowId ? goal : null;
  const goalDetail = useWorkflowDetail(goalId);
  const [history, setHistory] = useState<DoneRow[]>([]);
  const [ran, setRan] = useState<RanStep | null>(runId === null ? null : { runId, display: null });
  const [link, setLink] = useState<ChainLink>({
    actionType: workflowId,
    label: null,
    parent: null,
  });
  const [past, setPast] = useState<Past[]>([]);
  const [seeded, setSeeded] = useState(runId === null);
  const session = useSession(runId);
  const seedRunId = runId;

  useEffect(() => {
    if (seeded || seedRunId === null) return;
    if (session.isError) {
      setSeeded(true);
      return;
    }
    if (session.data === undefined) return;
    const steps = session.data?.steps ?? [];
    const at = steps.findIndex((entry) => entry.runId === seedRunId);
    const step = steps[at];
    if (step !== undefined) {
      setHistory(historyFromSession(steps, seedRunId));
      setLink({
        actionType: step.actionType,
        label: step.actionName,
        parent: seededParent(steps, at),
      });
      // Two history rows per earlier step, so going back lands on the run that
      // produced them instead of restarting the chain.
      setPast(
        steps.slice(0, at).map((earlier, index) => ({
          link: {
            actionType: earlier.actionType,
            label: earlier.actionName,
            parent: seededParent(steps, index),
          },
          ran: { runId: earlier.runId, display: null },
          historyLength: index * 2,
        })),
      );
    }
    setSeeded(true);
  }, [seeded, seedRunId, session.data, session.isError]);
  const progress =
    goal === null ? null : goalProgress(goalDetail.data, workflowId, link.actionType);
  // A goal that cannot load, or is this workflow, never guides; neither does one
  // whose path turns out not to hold the current step.
  const unusableGoal = goal !== null && (goal === workflowId || goalDetail.isError);
  const offGoalPath =
    seeded && goalId !== null && goalDetail.data !== undefined && progress === null;
  useEffect(() => {
    if (unusableGoal || offGoalPath) onGoalClear();
  }, [unusableGoal, offGoalPath, onGoalClear]);
  const active = useWorkflowDetail(link.actionType, (link.parent?.selectItems.length ?? 0) > 0);
  const activeRun = useRunResult(ran?.runId ?? null);
  const [activeConfig, activeResult] = active.data?.steps ?? [];
  const sessionParams =
    session.data?.steps.find((step) => step.runId === ran?.runId)?.params ?? null;
  const reloadedValues = useMemo(
    () =>
      activeConfig === undefined || sessionParams === null
        ? {}
        : fieldValuesFromParams(activeConfig.fields, sessionParams),
    [activeConfig, sessionParams],
  );
  const reloadedOptions = useFieldOptions(
    activeConfig,
    reloadedValues,
    dateWindow,
    accountIds,
    leadingKpi,
  );
  const reloaded =
    activeConfig === undefined || sessionParams === null
      ? null
      : describeValues(activeConfig, reloadedValues, reloadedOptions);
  const finishedRun = activeRun.data?.status === 'completed' ? activeRun.data : null;
  const activeRunning = runInFlight(
    ran !== null,
    activeRun.data?.status ?? null,
    activeRun.isError,
  );
  const rows = sessionMapRows(
    history,
    {
      key: `${link.actionType}-${history.length}`,
      configTitle: activeConfig?.title ?? link.label ?? root.data?.name ?? '',
      configurable:
        link.parent === null || activeConfig === undefined || activeConfig.fields.length > 0,
      resultTitle: activeResult?.title ?? 'Results',
      resultHint: activeResult?.description ?? null,
    },
    ran === null ? null : { runId: ran.runId, display: ran.display ?? reloaded },
    finishedRun === null
      ? null
      : {
          outcome:
            finishedRun.outcomeKind === null
              ? null
              : {
                  runId: finishedRun.runId,
                  name: finishedRun.outcomeTitle,
                  kind: finishedRun.outcomeKind,
                  date: null,
                  ...versionOf(finishedRun.actionType, link.parent, finishedRun.completedAt),
                },
          extraOutcomes: extraMapOutcomes(finishedRun, finishedRun.extraOutcomes, null),
        },
    link.parent === null || link.parent.selectItems.length > 0,
  );

  if (root.isPending || !seeded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (root.isError) {
    return (
      <Stack sx={{ gap: 3, alignItems: 'flex-start', px: GUTTER_X, py: 5 }}>
        <Alert severity="error">This workflow could not be loaded.</Alert>
        <Link component={RouterLink} to="/workflows" sx={{ fontSize: 13 }}>
          Back to workflows
        </Link>
      </Stack>
    );
  }

  const advance: Advance = (
    next,
    selectItems,
    params,
    doneRows,
    parentRunId,
    inherited,
    briefRunId,
  ) => {
    setPast((current) => [...current, { link, ran, historyLength: history.length }]);
    setHistory((current) => [...current, ...doneRows]);
    setRan(null);
    setLink({
      actionType: next.actionType,
      label: next.label,
      parent: { runId: parentRunId, assumed: false, briefRunId, selectItems, params, inherited },
    });
  };

  // A chained step with no run of its own keeps the session in the URL by its parent run.
  const changeRun = (next: RanStep | null): void => {
    setRan(next);
    onRunChange(next?.runId ?? link.parent?.runId ?? null);
  };

  const goBackTo = (flowIndex: number): void => {
    const previous = past[flowIndex];
    if (previous === undefined) return;
    AnalyticsService.track('Workflow - Chain Back', {
      workflowId,
      toActionType: previous.link.actionType,
    });
    setPast((current) => current.slice(0, flowIndex));
    setHistory((current) => current.slice(0, previous.historyLength));
    setLink(previous.link);
    setRan(previous.ran);
    onRunChange(previous.ran?.runId ?? previous.link.parent?.runId ?? null);
  };

  return (
    <Stack sx={{ flex: 1 }}>
      {preflightOnly ? null : (
        <Breadcrumb
          name={root.data.name}
          goal={progress}
          automation={session.data?.automation ?? null}
        />
      )}

      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <ActiveStep
            key={`${link.actionType}-${history.length}`}
            link={link}
            scope={session.data?.scope ?? null}
            ran={ran}
            params={sessionParams}
            onRan={changeRun}
            dateWindow={dateWindow}
            accountIds={accountIds}
            availablePlatforms={availablePlatforms}
            leadingKpi={leadingKpi}
            filters={filters}
            doneIndex={visibleRows(history).length + 1}
            before={
              preflightOnly ? null : (
                <WorkflowStepper
                  activeState={stepperState(activeRunning, activeRun.data?.status ?? null)}
                  {...(activeRunning
                    ? {}
                    : {
                        onEdit: (flowIndex: number) => {
                          if (flowIndex < past.length) {
                            goBackTo(flowIndex);
                            return;
                          }
                          changeRun(null);
                        },
                      })}
                  flows={stepperFlows(
                    rows,
                    [...past.map((entry) => entry.historyLength), history.length],
                    [...past.map((entry) => entry.link), link].map(
                      (entry) => entry.label ?? root.data.name,
                    ),
                  )}
                />
              )
            }
            onAdvance={advance}
            onRunStarted={onRunStarted}
            preferredActionType={progress?.next ?? null}
            goalPending={goalId !== null && goalDetail.isPending}
          />
        </Stack>

        {preflightOnly ? null : (
          <SessionMap
            rows={withOutcomeDates(visibleRows(rows), session.data?.steps ?? [])}
            saved={savedOutcomes(session.data?.steps ?? [], session.data?.asides)}
            scope={session.data?.scope ?? null}
            leadingKpi={leadingKpi}
            onEdit={() => changeRun(null)}
            editDisabled={activeRunning}
            footer={
              // Remounts per run, so the draft is read again once each run settles.
              activeRunning || session.data == null ? null : (
                <AutomateCard
                  key={ran?.runId ?? session.data.rootRunId}
                  sessionId={session.data.rootRunId}
                  accountCount={session.data.scope?.accountIds.length ?? 0}
                  onOpenAutomations={() =>
                    void navigate({ to: '/workflows', search: { tab: 'automations' } })
                  }
                />
              )
            }
          />
        )}
      </Stack>
    </Stack>
  );
}
